import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { companyCostCenterSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as companiesActionCreators from '../companies/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { CompanyCostCenter } from './model'
import { Company } from '../companies/model'

// Fetch

const fetchCompanyCostCentersEpic = (action$, store) => {
    const tableIdentifier = 'company_cost_centers_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `company-cost-centers/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(companyCostCenterSchema)
                        )

                        const companyCostCenters = normalizedEntitiesToRecordMap(
                            normalizedData.entities.company_cost_centers,
                            CompanyCostCenter,
                            normalizedData.result
                        )
                        const companies = normalizedEntitiesToRecordMap(normalizedData.entities.companies, Company)

                        const observables = [
                            Observable.of(actionCreators.fetchCompanyCostCentersFulfilled(companyCostCenters)),
                            Observable.of(companiesActionCreators.fetchCompaniesFulfilled(companies)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCompanyCostCenterFulfilled(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCompanyCostCenterEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().companies.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `company-cost-centers/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, companyCostCenterSchema)

                        const companyCostCenters = normalizedEntitiesToRecordMap(normalizedData.entities.company_cost_centers, CompanyCostCenter)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCompanyCostCentersFulfilled(companyCostCenters)),
                            Observable.of(actionCreators.fetchCompanyCostCenterFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCompanyCostCenterRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCompanyCostCenterEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().companies.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)

            let path = `company-cost-centers`
            let method = 'POST'

            if (values.id) {
                path = `company-cost-centers/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, companyCostCenterSchema)

                        const companyCostCenters = normalizedEntitiesToRecordMap(normalizedData.entities.company_cost_centers, CompanyCostCenter)
                        const companyCostCenter = companyCostCenters.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCompanyCostCenterFulfilled(companyCostCenter)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCompanyCostCenterRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCompanyCostCenterEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().companies.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `company-cost-centers/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCompanyCostCenterFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteCompanyCostCenterRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportCompanyCostCentersEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `company-cost-centers/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [
    fetchCompanyCostCentersEpic,
    fetchCompanyCostCenterEpic,
    saveCompanyCostCenterEpic,
    deleteCompanyCostCenterEpic,
    exportCompanyCostCentersEpic,
]
